import {
  addMonths,
  differenceInDays,
  format,
  formatDistanceToNowStrict,
  getDay,
  getDaysInMonth,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  subMonths,
} from "date-fns";
import { it } from "date-fns/locale";
import {
  type CurrentTimes,
  TIMEZONE_DATA,
  type TimezoneDisplay,
  type TimezoneInfo,
} from "@/constants/timezones";

const DEFAULT_LOCALE = it;

export const formatDeadline = (
  date: Date | undefined | string,
  options?: {
    precision?: "day" | "hour";
  },
) => {
  if (!date) return undefined;
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  if (!isValid(dateObj)) return undefined;
  if (options?.precision === "hour") {
    return format(dateObj, "dd/MM/yyyy HH:mm", { locale: DEFAULT_LOCALE });
  }
  return format(dateObj, "dd/MM/yyyy", { locale: DEFAULT_LOCALE });
};

export const formatDateAgo = (date: Date | undefined | string) => {
  if (!date) return undefined;
  const dateObj = date instanceof Date ? date : parseISO(date);
  if (!isValid(dateObj)) return undefined;
  const diffDays = differenceInDays(new Date(), dateObj);
  return `${diffDays}`;
};

export const formatTimezoneDisplay = (
  timezone: TimezoneInfo,
  currentTimes: CurrentTimes,
  detectedTimezone: string,
): TimezoneDisplay => {
  const offset: string =
    timezone.offset >= 0 ? `+${timezone.offset}` : timezone.offset.toString();
  const time: string = currentTimes[timezone.value] || "";
  const isDetected: boolean = timezone.value === detectedTimezone;

  return {
    main: `(UTC${offset}) ${timezone.label} - ${timezone.city}`,
    time: time,
    isDetected: isDetected,
  };
};

export const getTimezoneFromValue = (value: string | null): TimezoneInfo => {
  if (!value)
    return {
      value: "",
      label: "",
      city: "",
      offset: 0,
    };
  const allTimezones = Object.values(TIMEZONE_DATA).flat();
  return (
    allTimezones.find((tz) => tz.value === value) ?? {
      value: "",
      label: "",
      city: "",
      offset: 0,
    }
  );
};

export const formatDate = (date: Date | string) => {
  const d = date instanceof Date ? date : parseISO(date);
  return format(d, "dd/MM/yyyy", { locale: DEFAULT_LOCALE });
};

export const getDateFromDateString = (date?: string | null) => {
  if (!date) return null;
  const parsed = parseISO(date);
  return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : null;
};

/**
 * Parses a string into a Date. Returns null if the input is null or the result is Invalid Date.
 */
export const parseDateOrNull = (value?: string | null | Date): Date | null => {
  if (value === null || value === "" || value === undefined) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return isValid(parsed) ? parsed : null;
};

export const formatReadableDate = (date: Date | string) => {
  const d = date instanceof Date ? date : new Date(date);
  return format(d, "d MMMM yyyy", { locale: DEFAULT_LOCALE });
};

/**
 * La data del calendario, senza il fuso.
 *
 * Le date del curriculum arrivano come mezzanotte ISO (`2024-08-01T00:00:00Z`),
 * e `new Date` di quella stringa è il 31 luglio a ovest di Greenwich. Su
 * un'etichetta al giorno lo scarto costa un giorno; su una al mese costa un mese
 * intero, quindi qui il calendario si legge dalla stringa e non dall'istante.
 */
const toCalendarDate = (value: Date | string): Date => {
  if (value instanceof Date) return value;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return iso
    ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    : new Date(value);
};

/**
 * Un periodo, non un giorno: «Settembre 2025».
 *
 * Esperienze e titoli di studio sono quasi tutti importati da LinkedIn, che
 * conosce soltanto mese e anno e scrive il giorno 1. «1 settembre 2025» dava
 * per esatto un dato che nessuno ha mai inserito — e su un periodo di carriera
 * il giorno non aggiunge niente comunque.
 *
 * Maiuscola iniziale perché è un'etichetta a sé, non una parola dentro una
 * frase.
 */
export const formatMonthYearReadable = (date: Date | string) => {
  const label = format(toCalendarDate(date), "MMMM yyyy", {
    locale: DEFAULT_LOCALE,
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/**
 * Formats an ISO date as a relative, Italian-localized distance from now
 * (e.g. "3 giorni fa", "1 mese fa"). Used for the Talent Pool card's save date.
 *
 * Returns `null` — never "NaN giorni fa" — when the input is missing (legacy
 * pool rows have no `created_at`) or unparseable, so callers can simply omit the
 * segment. Uses the strict variant so the label reads "1 mese fa" rather than
 * date-fns' approximate "circa 1 mese fa".
 */
export const formatRelativeDaysIt = (value?: string | null): string | null => {
  if (value == null || value === "") return null;
  const parsed = parseISO(value);
  if (!isValid(parsed)) return null;
  return formatDistanceToNowStrict(parsed, {
    addSuffix: true,
    locale: DEFAULT_LOCALE,
  });
};

// --- Calendar / DatePicker utilities ---

/**
 * Returns calendar grid days for a given month (null for padding, number for actual days).
 * Week starts on Sunday (getDay() returns 0 for Sunday).
 */
export function getCalendarDays(
  year: number,
  month: number,
): (number | null)[] {
  const firstDay = startOfMonth(new Date(year, month));
  const startPadding = getDay(firstDay);
  const daysInMonth = getDaysInMonth(firstDay);

  const days: (number | null)[] = [];

  for (let i = 0; i < startPadding; i++) {
    days.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(d);
  }

  return days;
}

/**
 * Returns short weekday names for the given locale.
 */
export function getWeekdayNames(locale = DEFAULT_LOCALE): string[] {
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(2024, 0, i + 1);
    return format(date, "EEE", { locale });
  });
}

/**
 * Formats a date as month name only for calendar header (e.g. "gennaio").
 */
export function formatMonth(date: Date, locale = DEFAULT_LOCALE): string {
  return format(date, "MMMM", { locale });
}

/**
 * Formats a date as "month year" for calendar header (e.g. "gennaio 2025").
 */
export function formatMonthYear(date: Date, locale = DEFAULT_LOCALE): string {
  return format(date, "MMMM yyyy", { locale });
}

/**
 * Returns years for the date picker year dropdown.
 * Uses minDate/maxDate when provided, otherwise a sensible range (e.g. current ± 100).
 */
export function getYearsForPicker(
  minDate?: Date | null,
  maxDate?: Date | null,
): number[] {
  const currentYear = new Date().getFullYear();
  const minYear = minDate ? minDate.getFullYear() : currentYear - 100;
  const maxYear = maxDate ? maxDate.getFullYear() : currentYear + 10;
  const years: number[] = [];
  for (let y = minYear; y <= maxYear; y++) {
    years.push(y);
  }
  return years;
}

/**
 * Adds months to a date. Use for calendar navigation.
 */
/**
 * Normalizes a date to midnight for date-only comparison.
 */
export { addMonths, startOfDay, subMonths };

/**
 * Checks if a date falls within min/max range (date-only comparison).
 * Uses timestamp comparison so only the calendar day matters, not the time.
 */
export function isDateInRange(
  date: Date,
  minDate?: Date | null,
  maxDate?: Date | null,
): boolean {
  const dateOnly = startOfDay(date);
  const dateTime = dateOnly.getTime();
  if (minDate && minDate != null) {
    const minTime = startOfDay(minDate).getTime();
    if (dateTime < minTime) return false;
  }
  if (maxDate && maxDate != null) {
    const maxTime = startOfDay(maxDate).getTime();
    if (dateTime > maxTime) return false;
  }
  return true;
}
